import { Client, type MessageContent, type Message } from '@AlidaPackages/ailabs-client';
import { readonly, writable } from 'svelte/store';
import type { AssistantClient, AssistantClientConfig } from '$lib/types/AssistantClient';
import { Guid } from '@AlidaPackages/guid';
import { getAiLabsBaseUrl } from '$lib/utils/getAiLabsBaseUrl';

export function createAssistantClient(config: AssistantClientConfig) {
	const client = new Client(getAiLabsBaseUrl(config.pod), config.appName, config.applicationId);
	const vcTraceId = new Guid(crypto.randomUUID()).toString();
	let conversationId: string | null = null;
	let isCancelled = false;

	const state = {
		messages: writable<Message[]>([]),
		loading: writable(false),
		hasError: writable(false),
		isJwtExpired: writable(false),
		cancelled: writable(false),
	};

	async function abort() {
		isCancelled = true;
		state.cancelled.set(true);
		state.loading.set(false);

		if (conversationId) {
			const jwt = await config.getJwt();
			await client.stopMessage(conversationId, jwt, vcTraceId);
		}
	}

	async function sendMessage(messages: MessageContent[]) {
		// Reset cancellation flag for new request
		isCancelled = false;
		state.cancelled.set(false);

		try {
			const jwt = await config.getJwt();
			state.loading.set(true);
			state.hasError.set(false);
			state.isJwtExpired.set(false);

			if (!conversationId) {
				const conversation = await client.createConversation(jwt, vcTraceId, {
					agentId: config.agentName,
				});
				conversationId = conversation.id;
			}

			const response = await client.sendMessage(conversationId, messages, jwt, vcTraceId);

			// Check if the request was cancelled
			if (isCancelled) {
				return;
			}

			if (!response || response.length === 0) {
				throw new Error('No response received from assistant');
			}

			const userMessage = { role: 'user', content: messages } satisfies Message;
			const assistantMessage = {
				role: 'assistant',
				content: response,
			} satisfies Message;

			state.messages.update((currentMessages) => [
				...currentMessages,
				userMessage,
				assistantMessage,
			]);
		} catch (error) {
			// Don't set error state if the request was cancelled
			if (!isCancelled) {
				state.hasError.set(true);

				if (error instanceof Error && error.message.includes('Unauthorized')) {
					state.isJwtExpired.set(true);
				}
			}
		} finally {
			state.loading.set(false);
		}
	}

	return {
		state: {
			messages: readonly(state.messages),
			loading: readonly(state.loading),
			hasError: readonly(state.hasError),
			isJwtExpired: readonly(state.isJwtExpired),
			cancelled: readonly(state.cancelled),
		},
		sendMessage,
		abort,
	} satisfies AssistantClient;
}
